import { ModuleDocContent } from "./docsData";

export const administrationModuleDocs: Record<string, ModuleDocContent> = {
  "user-management": {
    title: "Employee Management",
    subtitle: "Manage Platform Users",
    color: "amber",
    badge: "User Management",
    description:
      "View, create, edit, and manage all platform employees. Search and filter by department, role, and status. Supports bulk CSV import/export, password resets, and user status management.",
    content: `
<div class="sz-callout sz-callout-info">
  Employee Management is your central hub for managing all platform users. View employee details, manage statuses, assign courses, and perform bulk operations from a single interface.
</div>

<div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/b03d3e28-4d8c-4647-9b76-7f7570431eb5.png" alt="Employee Management" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Employee management with filters</p></div>

<section>
  <h2>Header Statistics</h2>
  <p>Three summary cards in the gradient header:</p>
  <table>
    <thead>
      <tr>
        <th>Card</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Total Employees</strong></td>
        <td>Count of all employees in the platform.</td>
      </tr>
      <tr>
        <td><strong>Active Employees</strong></td>
        <td>Number of employees with active status.</td>
      </tr>
      <tr>
        <td><strong>Pending Invites</strong></td>
        <td>Number of invitations that have not yet been accepted.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Search &amp; Filters</h2>
  <table>
    <thead>
      <tr>
        <th>Control</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Search</strong></td>
        <td>Search by name, email, or employee ID.</td>
      </tr>
      <tr>
        <td><strong>Department filter</strong></td>
        <td>Dropdown to filter by department (All Departments + available groups).</td>
      </tr>
      <tr>
        <td><strong>Status filter</strong></td>
        <td>Filter by: <code>Active</code>, <code>Inactive</code>, <code>Suspended</code>, <code>Locked</code>, <code>Archived</code>, <code>Deleted</code>.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Employee Table</h2>
  <table>
    <thead>
      <tr>
        <th>Column</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>User info</strong></td>
        <td>Name and avatar.</td>
      </tr>
      <tr>
        <td><strong>Department</strong></td>
        <td>Assigned department/group.</td>
      </tr>
      <tr>
        <td><strong>Role(s)</strong></td>
        <td>Assigned roles (can have multiple).</td>
      </tr>
      <tr>
        <td><strong>Email</strong></td>
        <td>User email address.</td>
      </tr>
      <tr>
        <td><strong>Status</strong></td>
        <td>Color-coded status badge.</td>
      </tr>
      <tr>
        <td><strong>Last Activity</strong></td>
        <td>Timestamp of last platform activity.</td>
      </tr>
      <tr>
        <td><strong>Actions</strong></td>
        <td>Edit, Reset Password, View Details, Delete.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Actions &amp; Modals</h2>
  <table>
    <thead>
      <tr>
        <th>Action</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Add Employee</strong></td>
        <td>Form with email, name, groups, roles, and password fields.</td>
      </tr>
      <tr>
        <td><strong>Export</strong></td>
        <td>Download employee data as CSV or Excel.</td>
      </tr>
      <tr>
        <td><strong>Bulk Upload</strong></td>
        <td>CSV import with duplicate handling. Access Import History to review past uploads and individual record details.</td>
      </tr>
      <tr>
        <td><strong>Reset Password</strong></td>
        <td>Send a password reset to the selected user.</td>
      </tr>
      <tr>
        <td><strong>Edit User</strong></td>
        <td>Update profile details, department, and roles.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Common use cases</h2>
  <div class="sz-use-cases">
    <div class="sz-use-case">
      <div class="sz-use-case-title">Employee onboarding</div>
      <div class="sz-use-case-flow">
        Click <strong>Add Employee</strong> to create individual accounts, or use <strong>Bulk Upload</strong> to import a CSV of new hires. Assign departments and roles during creation.
      </div>
    </div>
    <div class="sz-use-case">
      <div class="sz-use-case-title">Offboarding</div>
      <div class="sz-use-case-flow">
        Search for the departing employee, click <strong>Edit</strong>, and set status to <code>Inactive</code> or <code>Archived</code>. Access is revoked but all progress and certifications are preserved.
      </div>
    </div>
  </div>
</section>

<div class="sz-callout sz-callout-tip">
  <div class="sz-callout-label">Tip</div>
  Use the <strong>Bulk Upload</strong> feature with the <strong>Import History</strong> view to track which uploads succeeded and which rows had errors.
</div>
`,
  },

  "group-management": {
    title: "Department Management",
    subtitle: "Organize Users into Departments",
    color: "amber",
    badge: "User Management",
    description:
      "Create and manage departments to organize users, assign training at scale, and track learning progress by team. View member stats, assignment counts, and completion breakdowns per department.",
    content: `
<div class="sz-callout sz-callout-info">
  Departments let you organize users into teams for bulk course assignments, targeted analytics, and streamlined management. Each department card shows member count, learning stats, and assignment totals.
</div>

<div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/882fd24c-fed2-4cca-88de-7404ac577d98.png" alt="Departments" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Department management</p></div>

<section>
  <h2>Header Statistics</h2>
  <p>Four summary cards in the gradient header:</p>
  <table>
    <thead>
      <tr>
        <th>Card</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Total Departments</strong></td>
        <td>Count of all departments.</td>
      </tr>
      <tr>
        <td><strong>Departments In Use</strong></td>
        <td>Departments that have at least one member.</td>
      </tr>
      <tr>
        <td><strong>Users with Departments</strong></td>
        <td>Number of users assigned to at least one department.</td>
      </tr>
      <tr>
        <td><strong>Total Assignments</strong></td>
        <td>Breakdown of Courses, Certifications, and Learning Paths assigned across all departments.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Department Cards</h2>
  <p>Departments are displayed as a card grid (responsive: 1 column on mobile, 2 on tablet, 3 on desktop). Each card shows:</p>
  <table>
    <thead>
      <tr>
        <th>Element</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Name &amp; Description</strong></td>
        <td>Department name and optional description text.</td>
      </tr>
      <tr>
        <td><strong>Member Stats</strong></td>
        <td>Breakdown: Completed, In Progress, Not Started, and Overdue member counts.</td>
      </tr>
      <tr>
        <td><strong>Assignment Counts</strong></td>
        <td>Number of courses, certifications, and learning paths assigned.</td>
      </tr>
      <tr>
        <td><strong>Actions</strong></td>
        <td>Edit and Delete buttons per department card.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Controls</h2>
  <table>
    <thead>
      <tr>
        <th>Control</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Search</strong></td>
        <td>Search departments by name.</td>
      </tr>
      <tr>
        <td><strong>Export</strong></td>
        <td>Download department data as CSV or Excel.</td>
      </tr>
      <tr>
        <td><strong>Add Department</strong></td>
        <td>Opens a form to create a new department with name and description.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Common use cases</h2>
  <div class="sz-use-cases">
    <div class="sz-use-case">
      <div class="sz-use-case-title">Department training</div>
      <div class="sz-use-case-flow">
        Create departments per team and assign department-specific courses. The card view lets you quickly see which departments have overdue or not-started members.
      </div>
    </div>
    <div class="sz-use-case">
      <div class="sz-use-case-title">Progress monitoring</div>
      <div class="sz-use-case-flow">
        Use the member stats on each card (Completed / In Progress / Not Started / Overdue) to monitor training progress at a glance. Export data for detailed analysis.
      </div>
    </div>
  </div>
</section>

<div class="sz-callout sz-callout-tip">
  <div class="sz-callout-label">Tip</div>
  Check the <strong>Overdue</strong> count on each department card regularly. A high overdue count signals departments that need follow-up reminders.
</div>
`,
  },

  "role-permission-mgmt": {
    title: "Role & Permission Management",
    subtitle: "Control Access & Capabilities",
    color: "amber",
    badge: "User Management",
    description:
      "Create and manage roles with assigned permissions. View role usage, course assignments, and certification counts. Assign granular permissions to each role.",
    content: `
<div class="sz-callout sz-callout-info">
  Roles control what users can access and do on the platform. Create custom roles, assign permissions via the dedicated permissions page, and track how many users and courses are associated with each role.
</div>

<div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/97e6cfe3-d878-4732-b15c-4f934daf37ac.png" alt="Roles and Permissions" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Role management</p></div>

<div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/8458f9e6-03ee-4493-97fe-5f1cbdc4fc7e.png" alt="Permission Management" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Permission assignment page</p></div>

<section>
  <h2>Role Table</h2>
  <table>
    <thead>
      <tr>
        <th>Column</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Role name</strong></td>
        <td>Role name, key, and last updated date.</td>
      </tr>
      <tr>
        <td><strong>Users</strong></td>
        <td>Number of users assigned to this role.</td>
      </tr>
      <tr>
        <td><strong>Courses</strong></td>
        <td>Number of courses assigned to this role.</td>
      </tr>
      <tr>
        <td><strong>Certifications</strong></td>
        <td>Number of certifications associated with this role.</td>
      </tr>
      <tr>
        <td><strong>Actions</strong></td>
        <td>Assign Permissions (shield icon), View Courses, Edit, Delete.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Controls</h2>
  <table>
    <thead>
      <tr>
        <th>Control</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Search</strong></td>
        <td>Debounced search across role names.</td>
      </tr>
      <tr>
        <td><strong>Export</strong></td>
        <td>CSV and Excel export options.</td>
      </tr>
      <tr>
        <td><strong>Add Role</strong></td>
        <td>Form to create a new role with name and description.</td>
      </tr>
      <tr>
        <td><strong>Assign Permissions</strong></td>
        <td>Navigates to a dedicated page where you toggle individual permissions by category.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Common use cases</h2>
  <div class="sz-use-cases">
    <div class="sz-use-case">
      <div class="sz-use-case-title">Creating a Manager role</div>
      <div class="sz-use-case-flow">
        Click <strong>Add Role</strong>, name it "Manager", then click <strong>Assign Permissions</strong> to toggle on team oversight, analytics, and course assignment permissions — without granting settings or enterprise access.
      </div>
    </div>
    <div class="sz-use-case">
      <div class="sz-use-case-title">Auditing role usage</div>
      <div class="sz-use-case-flow">
        Review the Users column to identify unused roles (0 users) that can be cleaned up, or heavily used roles that may need permission refinement.
      </div>
    </div>
  </div>
</section>

<div class="sz-callout sz-callout-tip">
  <div class="sz-callout-label">Tip</div>
  Use the <strong>View Courses</strong> action to see which courses are assigned at the role level. This helps ensure role-based training is properly configured.
</div>
`,
  },

  "invitations": {
    title: "User Invitations",
    subtitle: "Invite Users to the Platform",
    color: "amber",
    badge: "User Management",
    description:
      "Send individual or bulk email invitations, track status through the full lifecycle, and manage the onboarding flow for new users. View success rates and manage expired or cancelled invitations.",
    content: `
<div class="sz-callout sz-callout-info">
  <strong>User Invitations</strong> lets administrators send email invitations with pre-assigned departments and roles. Track acceptance rates, resend expired invitations, and manage the full invitation lifecycle.
</div>

<div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/b66dafa8-b8d9-4ff5-b2bf-e7aeca535a00.png" alt="Invitation Management" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: User invitations page</p></div>

<section>
  <h2>Header Statistics</h2>
  <p>Six summary cards in the gradient header:</p>
  <table>
    <thead>
      <tr>
        <th>Card</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Total Invites</strong></td>
        <td>Total number of invitations sent.</td>
      </tr>
      <tr>
        <td><strong>Pending</strong> <span style="color:#f59e0b;">(yellow)</span></td>
        <td>Invitations sent but not yet accepted.</td>
      </tr>
      <tr>
        <td><strong>Accepted</strong> <span style="color:#3b82f6;">(blue)</span></td>
        <td>Invitations accepted (user clicked the link).</td>
      </tr>
      <tr>
        <td><strong>Registered</strong> <span style="color:#10b981;">(green)</span></td>
        <td>Users who completed registration after accepting.</td>
      </tr>
      <tr>
        <td><strong>Expired</strong> <span style="color:#ef4444;">(red)</span></td>
        <td>Invitations that have passed their expiration date without being accepted.</td>
      </tr>
      <tr>
        <td><strong>Success Rate</strong></td>
        <td>Percentage of invitations that resulted in registration.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Filters &amp; Controls</h2>
  <table>
    <thead>
      <tr>
        <th>Control</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>Search</strong></td>
        <td>Search invitations by email address.</td>
      </tr>
      <tr>
        <td><strong>Status filter</strong></td>
        <td>Filter by: <code>Pending</code>, <code>Accepted</code>, <code>Registered</code>, <code>Expired</code>, <code>Cancelled</code>, <code>Inactive</code>.</td>
      </tr>
      <tr>
        <td><strong>Sort by</strong></td>
        <td>Sort by Date, Name, or Status.</td>
      </tr>
      <tr>
        <td><strong>Export</strong></td>
        <td>Download invitation data as CSV or Excel.</td>
      </tr>
      <tr>
        <td><strong>Invite Users</strong></td>
        <td>Opens the invitation form to send new invitations.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Invitations Table</h2>
  <table>
    <thead>
      <tr>
        <th>Column</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>User</strong></td>
        <td>Name and email of the invitee.</td>
      </tr>
      <tr>
        <td><strong>Departments</strong></td>
        <td>Assigned groups and roles for the invitee.</td>
      </tr>
      <tr>
        <td><strong>Status</strong></td>
        <td>Color-coded status badge.</td>
      </tr>
      <tr>
        <td><strong>Invited</strong></td>
        <td>Date the invitation was sent.</td>
      </tr>
      <tr>
        <td><strong>Expires</strong></td>
        <td>Expiration date of the invitation link.</td>
      </tr>
      <tr>
        <td><strong>Actions</strong></td>
        <td>Resend, View Details, Delete.</td>
      </tr>
    </tbody>
  </table>
</section>

<section>
  <h2>Common use cases</h2>
  <div class="sz-use-cases">
    <div class="sz-use-case">
      <div class="sz-use-case-title">New hire batch onboarding</div>
      <div class="sz-use-case-flow">
        Click <strong>Invite Users</strong>, enter emails with department and role assignments. New hires register via the invitation link and start with pre-configured access on first login.
      </div>
    </div>
    <div class="sz-use-case">
      <div class="sz-use-case-title">Following up on pending invites</div>
      <div class="sz-use-case-flow">
        Filter by <strong>Pending</strong> or <strong>Expired</strong> status to identify unregistered invitees. Use the <strong>Resend</strong> action to re-send with a fresh expiration. Monitor the <strong>Success Rate</strong> card to track improvement.
      </div>
    </div>
  </div>
</section>

<div class="sz-callout sz-callout-tip">
  <div class="sz-callout-label">Tip</div>
  Monitor the <strong>Success Rate</strong> card weekly. If it drops below 70%, consider resending expired invitations and shortening the time between invite and expected first login.
</div>
`,
  },
};
